// src/config.ts
import { Logger } from '../src/packages/logger/src/Logger';
import {
  applyWebexCloudOverride,
  resolveWebexCloudOverride,
} from './packages/widgets/src/utils/webexCloudOverride';

enum Hostname {
  Localhost = 'localhost',
  Integration = 'int-msteams-calling.webex.com',
  Production = 'msteams-calling.webex.com',
  FedRAMP = 'msteams-calling.webexgov.us',
  FedRAMPHigh = 'msteams-calling-high.webexgov.us',
  Default = 'default',
}

interface EnvironmentConfig {
    [hostname: string]: {
      APP_ENVIRONMENT: string;
      CLIENT_ID: string;
      REDIRECT_URI: string;
      WEBEX_CLIENT_ID: string;
      WEBEX_REDIRECT_URI: string;
      SCOPE: string;
      WEBEX_SCOPE: string;
      WEBEX_BASE_URL: string;
      JABBER_INTEGRATION_URL: string;
      // Optional override for Webex-side integration calls (/webex/*). When
      // unset, falls back to JABBER_INTEGRATION_URL. The GCC-High commercial
      // override sets both to commercial jabber-integration.
      WEBEX_INTEGRATION_URL?: string;
      GRAPH_INTEGRATION_URL: string;
      WEBEX_U2C_BASE_URL: string;
      // GCC High only: explicit msft-presence-service host. GCC High Microsoft
      // Teams users must be mapped by the `-high` presence deployment, the only
      // one wired to login.microsoftonline.us / graph.microsoft.us (so it can
      // validate the GCC High token and subscribe to Graph presence). When unset,
      // the presence host is discovered via Webex u2c, which is correct for
      // FedRAMP-moderate / commercial Webex orgs.
      MSFT_PRESENCE_SERVICE_URL?: string;
      HYDRA_SERVICE_URL: string;
      WEBEX_CF_BOOLEAN_VAL: string;
      INTEGRATION_APP_ID: string;
      WEBEX_ATTENDANT_CONSOLE : string;
      MS_LOGIN_BASE_URL: string;
      // Add other configuration keys as needed
    };
  }

  // Define your configurations based on hostname
  const environmentConfig: EnvironmentConfig = {
    [Hostname.Localhost]: {
      APP_ENVIRONMENT: 'Dev_Environment',
      CLIENT_ID: '60fb0bb0-9d27-4c9d-a116-e35a7e6b9431',
      REDIRECT_URI: 'https://localhost:8081/webexteams/auth-end/loader',
      WEBEX_CLIENT_ID: 'Cb573ac3100d8d2848c473d6792dcd615738aac32cfaa38c0f812cde1a5c3dd60',
      WEBEX_REDIRECT_URI: 'https://localhost:8081/webexteams/webex-auth/loader',
      SCOPE: 'https://graph.microsoft.com/.default offline_access',
      WEBEX_SCOPE:"spark:xsi spark:organizations_read spark:calls_read spark:calls_write broadworks-connector:user Identity:SCIM Identity:Config spark:all webexsquare:get_conversation",
      WEBEX_BASE_URL: 'https://integration.webexapis.com',
      GRAPH_INTEGRATION_URL: 'https://graph.microsoft.com',
      WEBEX_U2C_BASE_URL: 'https://u2c-intb.ciscospark.com/u2c/api/v1',
      HYDRA_SERVICE_URL: 'https://apialpha.ciscospark.com/v1/',
      JABBER_INTEGRATION_URL: 'https://msteamsauthtest.justrandomtests.com/jabber-integration/api/v1',
      INTEGRATION_APP_ID : '5b8191ed-9ce9-4eb4-b453-1b686812db2c',
      WEBEX_CF_BOOLEAN_VAL: 'false',
      WEBEX_ATTENDANT_CONSOLE : '',
      MS_LOGIN_BASE_URL: 'https://login.microsoftonline.com'
    },
    [Hostname.Integration]: {
      APP_ENVIRONMENT: 'Int_Environment',
      CLIENT_ID: 'e1a8806d-dc36-421b-8084-37745a19348f',
      REDIRECT_URI: 'https://int-msteams-calling.webex.com/webexteams/auth-end/loader',
      WEBEX_CLIENT_ID: 'Cb573ac3100d8d2848c473d6792dcd615738aac32cfaa38c0f812cde1a5c3dd60',
      WEBEX_REDIRECT_URI: 'https://int-msteams-calling.webex.com/webexteams/webex-auth/loader',
      SCOPE: 'https://graph.microsoft.com/.default offline_access',
      WEBEX_SCOPE: 'spark:xsi spark:organizations_read spark:calls_read spark:calls_write broadworks-connector:user spark:people_read Identity:SCIM Identity:Config bc-sp-standard:user bc-sp-basic:user webexsquare:get_conversation',
      WEBEX_BASE_URL: 'https://integration.webexapis.com',
      JABBER_INTEGRATION_URL: 'https://int-first-general.svc.webex.com/jabber-integration/api/v1',
      GRAPH_INTEGRATION_URL: 'https://graph.microsoft.com',
      WEBEX_U2C_BASE_URL: 'https://u2c-intb.ciscospark.com/u2c/api/v1',
      HYDRA_SERVICE_URL: 'https://apialpha.ciscospark.com/v1/',
      WEBEX_CF_BOOLEAN_VAL: 'false',
      INTEGRATION_APP_ID : '3a9d50af-bfa3-4952-8d47-88cbc1754ae5',
      WEBEX_ATTENDANT_CONSOLE : 'Y2lzY29zcGFyazovL3VzL0FQUExJQ0FUSU9OL2VkYjlmNjY1LTM4ZmEtNDMyZi1hM2Q4LTU0MmM2OGU0YzEzZQ',
      MS_LOGIN_BASE_URL: 'https://login.microsoftonline.com'
    },
    [Hostname.Production]: {
        APP_ENVIRONMENT: 'Prod_Environment',
        CLIENT_ID: '263d10a3-85d9-493b-9593-984d0e28a895',
        REDIRECT_URI: 'https://msteams-calling.webex.com/webexteams/auth-end/loader',
        WEBEX_CLIENT_ID: 'Cee6d7f0250fb07f4a789316363333d5b74d2ba2cb09a1d00e659d3368b47b5f4',
        WEBEX_REDIRECT_URI: 'https://msteams-calling.webex.com/webexteams/webex-auth/loader',
        SCOPE: 'https://graph.microsoft.com/.default offline_access',
        WEBEX_SCOPE: 'spark:xsi spark:organizations_read spark:calls_read spark:calls_write broadworks-connector:user Identity:SCIM Identity:Config spark:all bc-sp-standard:user bc-sp-basic:user webexsquare:get_conversation',
        WEBEX_BASE_URL: 'https://webexapis.com',
        JABBER_INTEGRATION_URL: 'https://jabber-integration-a.wbx2.com/jabber-integration/api/v1',
        GRAPH_INTEGRATION_URL: 'https://graph.microsoft.com',
        WEBEX_U2C_BASE_URL: 'https://u2c.wbx2.com/u2c/api/v1',
        HYDRA_SERVICE_URL: 'https://api.ciscospark.com/v1',
        WEBEX_CF_BOOLEAN_VAL: 'true',
        INTEGRATION_APP_ID : '0924e969-85d8-4acb-8687-faacd6abd228',
        WEBEX_ATTENDANT_CONSOLE : 'Y2lzY29zcGFyazovL3VzL0FQUExJQ0FUSU9OLzY2NGUzZmJhLWQxYTgtNGYyNi1hOTMyLTMxMTJlOTBiNzJmZA',
        MS_LOGIN_BASE_URL: 'https://login.microsoftonline.com'
    },

    [Hostname.FedRAMP]: {
      APP_ENVIRONMENT: 'Fedramp_Environment',
      CLIENT_ID: '0e57192c-2f84-41ea-8b3d-e9d194677f90',
      REDIRECT_URI: 'https://msteams-calling.webexgov.us/webexteams/auth-end/loader',
      // REDIRECT_URI: 'https://localhost:8081/webexteams/auth-end/loader',
      WEBEX_CLIENT_ID: 'C03721daee97fba55662d4ee2447cddb82bd166532c1e8126bfdb83034ae9372e',
      WEBEX_REDIRECT_URI: 'https://msteams-calling.webexgov.us/webexteams/webex-auth/loader',
      // WEBEX_REDIRECT_URI: 'https://localhost:8081/webexteams/webex-auth/loader',
      SCOPE: 'https://graph.microsoft.com/.default offline_access',
      WEBEX_SCOPE: 'spark:xsi spark:organizations_read spark:calls_read spark:calls_write broadworks-connector:user Identity:SCIM Identity:Config spark:all bc-sp-standard:user bc-sp-basic:user webexsquare:get_conversation',
      WEBEX_BASE_URL: 'https://api-usgov.webex.com',
      JABBER_INTEGRATION_URL: 'https://wxc-msteams-integration.gov.ciscospark.com/wxc-msteams-integration/api/v1',
      GRAPH_INTEGRATION_URL: 'https://graph.microsoft.com',
      WEBEX_U2C_BASE_URL: 'https://u2c.gov.ciscospark.com/u2c/api/v1',
      HYDRA_SERVICE_URL: 'https://api-usgov.webex.com/v1',
      WEBEX_CF_BOOLEAN_VAL: 'true',
      INTEGRATION_APP_ID : '0e57192c-2f84-41ea-8b3d-e9d194677f90',
      // TODO: Update the WEBEX_ATTENDANT_CONSOLE value for FedRAMP, the below value is currently from PROD:
      WEBEX_ATTENDANT_CONSOLE : 'Y2lzY29zcGFyazovL3VzL0FQUExJQ0FUSU9OLzY2NGUzZmJhLWQxYTgtNGYyNi1hOTMyLTMxMTJlOTBiNzJmZA',
      MS_LOGIN_BASE_URL: 'https://login.microsoftonline.com'
    },

    [Hostname.FedRAMPHigh]: {
      APP_ENVIRONMENT: 'Fedramp_High_Environment',
      CLIENT_ID: '3860b9c0-380e-4eda-aaad-d24f8940d20c',
      REDIRECT_URI: 'https://msteams-calling-high.webexgov.us/webexteams/auth-end/loader',
      WEBEX_CLIENT_ID: 'Cc91a39e78da3a3dca350aba8a261d9697e84bd738fa120c1d125c52809766593',
      WEBEX_REDIRECT_URI: 'https://msteams-calling-high.webexgov.us/webexteams/webex-auth/loader',
      SCOPE: 'https://graph.microsoft.us/.default offline_access',
      WEBEX_SCOPE: 'spark:xsi spark:organizations_read spark:calls_read spark:calls_write broadworks-connector:user Identity:SCIM Identity:Config spark:all bc-sp-standard:user bc-sp-basic:user webexsquare:get_conversation',
      WEBEX_BASE_URL: 'https://api-usgov.webex.com',
      JABBER_INTEGRATION_URL: 'https://wxc-msteams-integration-high.gov.ciscospark.com/wxc-msteams-integration/api/v1',
      GRAPH_INTEGRATION_URL: 'https://graph.microsoft.us',
      WEBEX_U2C_BASE_URL: 'https://u2c.gov.ciscospark.com/u2c/api/v1',
      MSFT_PRESENCE_SERVICE_URL: 'https://msft-presence-service-high.gov.ciscospark.com/msft-presence-service/api/v1',
      HYDRA_SERVICE_URL: 'https://api-usgov.webex.com/v1',
      WEBEX_CF_BOOLEAN_VAL: 'true',
      INTEGRATION_APP_ID : '3860b9c0-380e-4eda-aaad-d24f8940d20c',
      // TODO: Update the WEBEX_ATTENDANT_CONSOLE value for FedRAMPHigh, the below value is currently from PROD:
      WEBEX_ATTENDANT_CONSOLE : 'Y2lzY29zcGFyazovL3VzL0FQUExJQ0FUSU9OLzY2NGUzZmJhLWQxYTgtNGYyNi1hOTMyLTMxMTJlOTBiNzJmZA',
      MS_LOGIN_BASE_URL: 'https://login.microsoftonline.us'
    },

    [Hostname.Default]: {
      APP_ENVIRONMENT: 'Prod_Environment',
      CLIENT_ID: '263d10a3-85d9-493b-9593-984d0e28a895',
      REDIRECT_URI: 'https://msteams-calling.webex.com/webexteams/auth-end/loader',
      WEBEX_CLIENT_ID: 'Cee6d7f0250fb07f4a789316363333d5b74d2ba2cb09a1d00e659d3368b47b5f4',
      WEBEX_REDIRECT_URI: 'https://msteams-calling.webex.com/webexteams/webex-auth/loader',
      SCOPE: 'https://graph.microsoft.com/.default offline_access',
      WEBEX_SCOPE: 'spark:xsi spark:organizations_read spark:calls_read spark:calls_write broadworks-connector:user Identity:SCIM Identity:Config spark:all bc-sp-standard:user bc-sp-basic:user webexsquare:get_conversation',
      WEBEX_BASE_URL: 'https://webexapis.com',
      JABBER_INTEGRATION_URL: 'https://jabber-integration-a.wbx2.com/jabber-integration/api/v1',
      GRAPH_INTEGRATION_URL: 'https://graph.microsoft.com',
      WEBEX_U2C_BASE_URL: 'https://u2c.wbx2.com/u2c/api/v1',
      HYDRA_SERVICE_URL: 'https://api.ciscospark.com/v1',
      WEBEX_CF_BOOLEAN_VAL: 'true',
      INTEGRATION_APP_ID : '0924e969-85d8-4acb-8687-faacd6abd228',
      WEBEX_ATTENDANT_CONSOLE : '',
      MS_LOGIN_BASE_URL: 'https://login.microsoftonline.com'
    }
  };

  // Determine the current hostname
  const currentHostname = window.location.hostname;

  Logger.info(`Hostname is: ${currentHostname}`);

  // Get the correct configuration for the current hostname or the default configuration
  const baseConfig = environmentConfig[currentHostname] || environmentConfig['default'];

  /**
   * GCC-High package serving COMMERCIAL Webex account users.
   *
   * Only when the tab contentUrl carries `?webexCloud=commercial` is the Webex
   * backend switched to commercial and all integration APIs routed to commercial
   * `jabber-integration` with the customer GCC-High Azure app id.
   */
  const webexCloudOverride = resolveWebexCloudOverride(
    typeof window !== 'undefined' ? window.location.search : undefined,
  );

  const config = applyWebexCloudOverride(baseConfig, webexCloudOverride);

  Logger.info(
    `App environment resolved: ${config.APP_ENVIRONMENT}` +
    (webexCloudOverride ? ` (webexCloud override: ${webexCloudOverride})` : ''),
  );

  /**
   * Feature Flags for Webex Contacts Integration
   * Controls the behavior of personal contacts enrichment
   */
  export const CONTACTS_FEATURE_FLAGS = {
    // Enable/disable contacts enrichment feature
    ENABLE_CONTACTS_ENRICHMENT: true,
    
    // Enable automatic cache refresh when expired
    CONTACTS_AUTO_REFRESH: true,
    
    // Cache time-to-live in milliseconds (default: 24 hours)
    CONTACTS_CACHE_TTL_MS: 12 * 60 * 60 * 1000, // 12 hours
    
    // Enable telemetry/performance metrics
    CONTACTS_ENABLE_TELEMETRY: true,
  };

  export default config;
